import { SiloArtCanvas } from "@/components/SiloArtLayer";
import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { FreeNumberInput } from "@/components/ui/free-number-input";
import {
  Sparkle, ArrowLeft, CreditCard, CheckCircle, Warning, Clock, Storefront,
  ArrowSquareOut, Lock, ShieldCheck, PencilSimple, Trash, ArrowCounterClockwise,
  HeartStraight, FloppyDisk, User as UserIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, getUser, setUser, clearAuth } from "@/lib/api";
import { RoleGate } from "@/lib/useRoles";
import { YouTubeKeyCard } from "@/components/BillingControls";
import TrialBillingCard from "@/components/TrialBillingCard";
import KeySetupTabs from "@/components/KeySetupTabs";
import HeldCreditBanner from "@/components/HeldCreditBanner";
import FamilyStatusCard from "@/components/FamilyStatusCard";
import TwoFactorCard from "@/components/TwoFactorCard";
import ReportContactModal from "@/components/ReportContactModal";
import { FinishSetupModal } from "@/components/FinishSetupPill";
import TaxInfoPanel from "@/components/TaxInfoPanel";

const fmt$ = (cents) => `$${((cents || 0) / 100).toFixed((cents || 0) % 100 === 0 ? 0 : 2)}`;
const fmtBps = (bps) => `${(bps / 100).toFixed(2)}%`;

function Card({ title, subtitle, icon: Icon, children, testid }) {
  return (
    <div className="bg-white border hairline rounded-xl p-6 shadow-sm" data-testid={testid}>
      <div className="flex items-start gap-3 mb-4">
        {Icon && (
          <div className="w-10 h-10 rounded-md bg-[#F0F1EE] text-[#4A5D4E] flex items-center justify-center shrink-0">
            <Icon size={20} weight="duotone" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="font-serif-display text-xl text-[#1C1B1A]">{title}</h3>
          {subtitle && <p className="text-sm text-[#6B6A68] mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    active: { bg: "bg-[#E6F2E6]", text: "text-[#2F6B3A]", label: "Active", Icon: CheckCircle },
    past_due: { bg: "bg-[#FFF3DD]", text: "text-[#8A5A00]", label: "Past due · grace period", Icon: Clock },
    canceled: { bg: "bg-[#F3EDED]", text: "text-[#8A3B3B]", label: "Canceled", Icon: Warning },
    inactive: { bg: "bg-[#F0F1EE]", text: "text-[#6B6A68]", label: "Not subscribed", Icon: Warning },
  };
  const m = map[status] || map.inactive;
  const Icon = m.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium ${m.bg} ${m.text}`} data-testid={`platform-status-${status}`}>
      <Icon size={13} weight="duotone" /> {m.label}
    </span>
  );
}

// Feb 21, 2026 (eve) — Account info card.
// Two-field editor for the core account identifiers (name + email). The
// Dashboard greeting reads `user.name`; the login identifier is `user.email`.
// Name updates are immediate; email updates require a password confirm to
// stop a hijacked session from quietly swapping the login id.
// (Named "AccountInfoCard" to avoid collision with the long-form bio
// ProfileCard further down the file.)
function AccountInfoCard() {
  const stored = getUser();
  const [name, setName] = useState(stored?.name || "");
  const [email, setEmail] = useState(stored?.email || "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const nameDirty = (name || "").trim() !== (stored?.name || "").trim();
  const emailDirty = (email || "").trim().toLowerCase() !== (stored?.email || "").trim().toLowerCase();
  const dirty = nameDirty || emailDirty;

  const save = async () => {
    if (!dirty) return;
    if (emailDirty && !currentPassword) {
      toast.error("Enter your current password to change your email.");
      return;
    }
    setSaving(true);
    try {
      const body = {};
      if (nameDirty) body.name = name.trim();
      if (emailDirty) {
        body.email = email.trim().toLowerCase();
        body.current_password = currentPassword;
      }
      const r = await api.patch("/me/account", body);
      // Refresh the cached user so the Dashboard greeting + header email
      // re-render the next time the user navigates.
      if (r.data?.user) setUser(r.data.user);
      setCurrentPassword("");
      toast.success(emailDirty ? "Account updated. Use your new email to log in next time." : "Profile updated.");
      // Broadcast so any open Dashboard tab re-reads the cached user.
      window.dispatchEvent(new CustomEvent("mysilo:user-updated"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save your changes.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      title="Your profile"
      subtitle="The name that greets you on the dashboard, and the email you log in with."
      icon={UserIcon}
      testid="profile-card"
    >
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68]">Display name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="What should we call you?"
            data-testid="profile-name-input"
            className="mt-1 w-full bg-white border hairline rounded-md px-3 py-2 text-sm text-[#1C1B1A] focus:outline-none focus:border-[#4A5D4E]"
          />
          <span className="text-[11px] text-[#6B6A68] mt-1 block">
            Shows up as “Hello, <em className="text-[#4A5D4E]">{(name || "creator").split(" ")[0]}</em>” on your dashboard.
          </span>
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68]">Login email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            data-testid="profile-email-input"
            className="mt-1 w-full bg-white border hairline rounded-md px-3 py-2 text-sm text-[#1C1B1A] focus:outline-none focus:border-[#4A5D4E]"
          />
          <span className="text-[11px] text-[#6B6A68] mt-1 block">
            This is what you sign in with. Changing it requires your current password.
          </span>
        </label>
      </div>
      {emailDirty && (
        <label className="block mt-4 max-w-md" data-testid="profile-password-row">
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68]">Confirm with current password</span>
          <input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            placeholder="Your current password"
            data-testid="profile-current-password"
            className="mt-1 w-full bg-white border hairline rounded-md px-3 py-2 text-sm text-[#1C1B1A] focus:outline-none focus:border-[#4A5D4E]"
            autoComplete="current-password"
          />
        </label>
      )}
      <div className="mt-5 flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          data-testid="profile-save-btn"
          className="text-sm px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
        >
          <FloppyDisk size={14} weight="duotone" /> {saving ? "Saving…" : "Save changes"}
        </button>
        {dirty && !saving && (
          <button
            type="button"
            onClick={() => { setName(stored?.name || ""); setEmail(stored?.email || ""); setCurrentPassword(""); }}
            data-testid="profile-cancel-btn"
            className="text-xs text-[#6B6A68] hover:text-[#1C1B1A]"
          >
            Cancel
          </button>
        )}
      </div>
    </Card>
  );
}

// Feb 23, 2026 — change-password card. Standard 3-field flow: current,
// new, confirm. Eye toggle on the new-password field while typing so the
// user can verify they didn't fat-finger it. We never SHOW the existing
// password — passwords are stored as one-way bcrypt hashes (correct &
// industry-standard); if they forget it, "Forgot password?" emails a
// reset link.
function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showNext, setShowNext] = useState(false);
  const [saving, setSaving] = useState(false);

  const reset = () => { setCurrent(""); setNext(""); setConfirm(""); setShowNext(false); };

  const submit = async () => {
    if (!current) { toast.error("Enter your current password."); return; }
    if (next.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    if (next === current) { toast.error("New password must be different from the current one."); return; }
    if (next !== confirm) { toast.error("The new passwords don't match."); return; }
    setSaving(true);
    try {
      await api.post("/me/change-password", { current_password: current, new_password: next });
      toast.success("Password updated.");
      reset();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update password.");
    } finally { setSaving(false); }
  };

  return (
    <Card title="Change password" subtitle="Used to sign in. Choose something at least 8 characters and don't reuse it on other sites." icon={Lock} testid="change-password-card">
      <div className="space-y-3 max-w-md">
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68]">Current password</span>
          <input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            data-testid="change-pw-current"
            autoComplete="current-password"
            placeholder="Your current password"
            className="mt-1 w-full px-3 py-2 border hairline rounded-md text-sm bg-white focus:outline-none focus:border-[#4A5D4E]"
          />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68]">New password</span>
          <div className="mt-1 relative">
            <input
              type={showNext ? "text" : "password"}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              data-testid="change-pw-new"
              autoComplete="new-password"
              placeholder="At least 8 characters"
              className="w-full px-3 py-2 pr-10 border hairline rounded-md text-sm bg-white focus:outline-none focus:border-[#4A5D4E]"
            />
            <button
              type="button"
              onClick={() => setShowNext((v) => !v)}
              data-testid="change-pw-eye"
              className="absolute inset-y-0 right-2 my-auto text-[#6B6A68] hover:text-[#1C1B1A]"
              aria-label={showNext ? "Hide password" : "Show password"}
              title={showNext ? "Hide" : "Show"}
            >
              {showNext ? "Hide" : "Show"}
            </button>
          </div>
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68]">Confirm new password</span>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            data-testid="change-pw-confirm"
            autoComplete="new-password"
            placeholder="Type it again"
            className="mt-1 w-full px-3 py-2 border hairline rounded-md text-sm bg-white focus:outline-none focus:border-[#4A5D4E]"
          />
        </label>
        <p className="text-[11px] text-[#6B6A68]">
          Forgot what your current password is? Use <Link to="/forgot-password" className="underline">Forgot password</Link> to email yourself a reset link instead.
        </p>
        <div className="flex items-center gap-3 pt-1">
          <button
            onClick={submit}
            disabled={saving || !current || !next || !confirm}
            data-testid="change-pw-save"
            className="px-5 py-2.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] text-sm disabled:opacity-50 flex items-center gap-1.5"
          >
            <FloppyDisk size={14} weight="duotone" /> {saving ? "Saving…" : "Save new password"}
          </button>
          {(current || next || confirm) && !saving && (
            <button type="button" onClick={reset} data-testid="change-pw-cancel" className="text-xs text-[#6B6A68] hover:text-[#1C1B1A]">
              Cancel
            </button>
          )}
        </div>
      </div>
    </Card>
  );
}

export default function AccountSettings() {
  const user = getUser();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [cfg, setCfg] = useState(null);
  const [silos, setSilos] = useState([]);
  const [savingSilo, setSavingSilo] = useState(null);
  const [busy, setBusy] = useState(false);
  // Jun 2026 (Randy) — one-time onboarding CTA, shown the FIRST time a brand-new
  // creator lands in Account & Billing right after the platform subscription clears.
  const [showWelcomeCta, setShowWelcomeCta] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([
        api.get("/stripe/config"),
        api.get("/silos"),
      ]);
      setCfg(c.data);
      setSilos(s.data);
    } catch (e) {
      if (e.response?.status === 401) { clearAuth(); nav("/login"); return; }
      toast.error(e.response?.data?.detail || "Failed to load account settings.");
    }
  }, [nav]);

  useEffect(() => { load(); }, [load]);

  // iter-40f — Listen for silo-updated broadcasts (e.g. slug refresh from
  // the rename audit in SiloSettings) so the Fan Pricing card stops showing
  // the old URL without requiring a full page reload.
  useEffect(() => {
    const onSiloUpdated = () => { load(); };
    window.addEventListener("mysilo:silo-updated", onSiloUpdated);
    return () => window.removeEventListener("mysilo:silo-updated", onSiloUpdated);
  }, [load]);

  // When we return from Stripe (success / connect_return / refresh), re-sync Connect status
  // so the UI flips from "Not onboarded" to "Connected" without a manual refresh.
  useEffect(() => {
    const flag = params.get("stripe");
    if (!flag) return;
    const sessionId = params.get("session_id");
    if (flag === "platform_success") {
      // Confirm the upgrade right here so it lands even when Stripe's webhook
      // is delayed or undelivered (e.g. test mode). Backend is idempotent.
      (async () => {
        try {
          if (sessionId) {
            const r = await api.post("/stripe/platform/confirm", { session_id: sessionId });
            if (r.data?.activated) {
              toast.success("You're now a Creator — subscription active.");
              // First (and only automatic) time: explain the platform plan isn't the whole
              // story — they still need a voucher or their own key. Reuses the exact
              // same explainer the dashboard "Finish setup" alert opens.
              setShowWelcomeCta(true);
            } else {
              toast("Payment received — finishing activation, this can take a moment.", { icon: "⏳" });
            }
          } else {
            toast.success("Platform subscription activated.");
          }
        } catch {
          toast.error("We couldn't confirm the upgrade automatically. Refresh in a moment.");
        }
        try { await load(); } catch { /* noop */ }
      })();
    } else {
      if (flag === "connect_return") toast.success("Stripe Express onboarding complete.");
      else if (flag === "cancelled") toast("Stripe flow cancelled.", { icon: "↩" });
      else if (flag === "refresh") toast.error("Stripe needed more info. Please continue onboarding.");
      (async () => {
        try { await api.get("/stripe/connect/status"); await load(); } catch { /* noop */ }
      })();
    }
    // strip param from URL
    params.delete("stripe");
    params.delete("session_id");
    setParams(params, { replace: true });
  }, [params, setParams, load]);

  // Jun 2026 — `?focus=byok` / `?focus=vouchers` deep-link (from the "Finish
  // setting up" pill, VoiceRequestBanner, and the voice "Do it now" CTA) scrolls
  // straight to the Key Setup / Vouchers / Trackers area once the page loads.
  // `vouchers` also opens KeySetupTabs on the Vouchers tab.
  const focusParam = params.get("focus");
  const focusKeyArea = focusParam === "byok" || focusParam === "vouchers";
  useEffect(() => {
    if (!focusKeyArea || !cfg) return;
    const el = document.getElementById("account-key-setup");
    if (el) {
      const t = setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
      return () => clearTimeout(t);
    }
  }, [focusKeyArea, cfg]);

  const startPlatformCheckout = async () => {
    setBusy(true);
    try {
      const r = await api.post("/stripe/platform/checkout", { origin_url: window.location.origin, return_path: window.location.pathname });
      // Feb 24, 2026 (Randy pt-founder-bypass) — if the backend
      // recognized this as a founder short-circuit, no Stripe URL
      // was returned. Show a friendly confirmation and refresh the
      // profile so the "active" state renders immediately.
      if (r.data?.already_granted) {
        toast.success(r.data.message || "Founder access — no charge required.");
        try { window.dispatchEvent(new CustomEvent("profile-updated")); } catch { /* fine */ }
        setBusy(false);
        return;
      }
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout.");
      setBusy(false);
    }
  };

  const openBillingPortal = async () => {
    setBusy(true);
    try {
      const r = await api.post("/stripe/platform/portal", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open billing portal.");
      setBusy(false);
    }
  };

  const startConnectOnboarding = async () => {
    setBusy(true);
    try {
      const r = await api.post("/stripe/connect/start", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start Stripe onboarding.");
      setBusy(false);
    }
  };

  // Feb 21, 2026 (late eve) — pre-fetch the Express dashboard URL once
  // Connect is ready so we can render a real <a target="_blank"> instead
  // of a JS popup (which iOS Safari was blocking → "white page" bug).
  // Feb 23, 2026 — connectStatus declaration moved above this useEffect
  // to fix a temporal-dead-zone ReferenceError that surfaced under React
  // strict mode (the dep-array reference was evaluating before the const
  // was initialized).
  const connectStatus = cfg?.stripe_account_id
    ? (cfg.charges_enabled ? "ready" : "pending")
    : "none";
  const [expressDashUrl, setExpressDashUrl] = useState(null);
  useEffect(() => {
    if (connectStatus !== "ready") return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.post("/stripe/connect/dashboard");
        if (!cancelled && r.data?.url) setExpressDashUrl(r.data.url);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [connectStatus]);

  const saveSiloPrice = async (silo, newCents, newTier) => {
    setSavingSilo(silo.id);
    try {
      const r = await api.patch(`/silos/${silo.id}`, {
        subscriber_price_cents: newCents,
        access_tier: newTier,
      });
      setSilos((prev) => prev.map((s) => s.id === silo.id ? r.data : s));
      toast.success(`Updated "${silo.name}" pricing.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save price.");
    } finally { setSavingSilo(null); }
  };

  const gracePeriodDaysLeft = (() => {
    if (!cfg?.grace_period_started_at || !cfg?.grace_period_days) return null;
    const started = new Date(cfg.grace_period_started_at).getTime();
    const expiresAt = started + cfg.grace_period_days * 24 * 60 * 60 * 1000;
    const daysLeft = Math.ceil((expiresAt - Date.now()) / (24 * 60 * 60 * 1000));
    return Math.max(0, daysLeft);
  })();

  if (!cfg) {
    return <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center text-[#6B6A68]">Loading account settings…</div>;
  }

  return (
    <div className="min-h-screen bg-[#FDFCF8] relative isolate" data-testid="account-settings-page">
      <SiloArtCanvas />
      {showWelcomeCta && (
        <FinishSetupModal
          onClose={() => setShowWelcomeCta(false)}
          onStart={() => {
            setShowWelcomeCta(false);
            const el = document.querySelector('[data-testid="account-settings-byok-section"]');
            el?.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
        />
      )}
      <header className="border-b hairline bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="p-2 rounded-md text-[#6B6A68] hover:bg-[#F0F1EE]" data-testid="back-to-dashboard">
              <ArrowLeft size={18} />
            </Link>
            {/* iter-35e: logo always routes to landing. */}
            <Link to="/" data-testid="logo-home" className="w-9 h-9 rounded-full bg-[#1C1B1A] text-[#FDFCF8] flex items-center justify-center hover:opacity-80 transition-opacity">
              <Sparkle size={18} weight="duotone" />
            </Link>
            <div>
              <div className="font-serif-display text-xl leading-none">Account & Billing</div>
              <div className="text-[10px] text-[#6B6A68] tracking-[0.25em] uppercase mt-1">Platform plan · Stripe · Fan pricing</div>
            </div>
          </div>
          <span className="text-xs text-[#6B6A68]">{user?.email}</span>
        </div>
      </header>

      {/* Iter160n (Randy pt-siloart-loosetext-categorical) — every themed
          page's <main> gets `.siloart-copy` so the loose-text rule catches
          headings + descriptions automatically. */}
      <main className="siloart-copy max-w-5xl mx-auto px-6 py-10 space-y-8">
        <HeldCreditBanner />
        <FamilyStatusCard />
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-[#1C1B1A]">Account & Billing</h1>
          <p className="mt-2 text-[#6B6A68] max-w-2xl">
            Your platform subscription, your Stripe Connect onboarding, and the per-silo fan
            pricing that drives your revenue — all in one place.
          </p>
        </motion.div>

        {/* Feb 21, 2026 (eve) — Account info card. Lets the user fix the
            "Hello, <name>" greeting on the dashboard and correct a typo'd
            email. Email change requires the current password. */}
        <AccountInfoCard />

        {/* Feb 23, 2026 — Change password card, slotted right under the
            email change so the two "credential" controls live together. */}
        <ChangePasswordCard />

        {/* Jun 2026 — Two-factor authentication (TOTP). Sits with the other
            credential controls. */}
        <TwoFactorCard />


        {/* Platform Plan ===================================================== */}
        {/* Oct 2026 (TRIAL_PLAN.md §8.8) — the trial sits ABOVE the subscription
            card: while it runs, it IS the billing relationship. */}
        <TrialBillingCard />
        <Card title="Platform subscription" subtitle={`${fmt$(cfg.your_platform_fee_cents)} / month · 3 silos, Workbench, YouTube tools, and Tone Architect.`} icon={CreditCard} testid="platform-plan-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <StatusPill status={cfg.platform_subscription_status || "inactive"} />
              {cfg.has_platform_fee_override && (
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#4A5D4E] px-2 py-1 rounded border hairline">
                  <Lock size={10} weight="duotone" className="inline mb-0.5" /> Custom rate
                </span>
              )}
              {gracePeriodDaysLeft !== null && (
                <span className="text-xs text-[#C16E5A] font-medium" data-testid="grace-period-badge">
                  {gracePeriodDaysLeft} day{gracePeriodDaysLeft === 1 ? "" : "s"} left in grace period
                </span>
              )}
            </div>
            <div className="flex gap-2">
              {cfg.platform_subscription_status === "active" ? (
                <button onClick={openBillingPortal} disabled={busy} data-testid="open-billing-portal"
                  className="text-sm px-4 py-2 rounded-md border hairline bg-white text-[#1C1B1A] hover:border-[#4A5D4E] hover:text-[#4A5D4E] flex items-center gap-1.5 disabled:opacity-60">
                  <ArrowSquareOut size={14} /> Manage subscription
                </button>
              ) : (
                <button onClick={startPlatformCheckout} disabled={busy} data-testid="start-platform-checkout"
                  className="text-sm px-5 py-2.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1.5 disabled:opacity-60 shadow-sm">
                  <CreditCard size={14} weight="duotone" /> Subscribe · {fmt$(cfg.your_platform_fee_cents)}/mo
                </button>
              )}
            </div>
          </div>
        </Card>

        {/* Stripe Connect Express =========================================== */}
        <Card
          title="Your Stripe account (Express Connect)"
          subtitle="Required only if you want to charge fans. Fans pay you directly; My Silo takes a small application fee."
          icon={Storefront}
          testid="stripe-connect-card"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              {connectStatus === "ready" && (
                <div className="flex items-center gap-2 text-[#2F6B3A]" data-testid="connect-status-ready">
                  <CheckCircle size={16} weight="duotone" /> <span className="text-sm font-medium">Onboarded — ready to accept fan subscriptions.</span>
                </div>
              )}
              {connectStatus === "pending" && (
                <div className="flex items-center gap-2 text-[#8A5A00]" data-testid="connect-status-pending">
                  <Clock size={16} weight="duotone" /> <span className="text-sm font-medium">Stripe needs more information.</span>
                </div>
              )}
              {connectStatus === "none" && (
                <div className="flex items-center gap-2 text-[#6B6A68]" data-testid="connect-status-none">
                  <Warning size={16} weight="duotone" /> <span className="text-sm">You haven't connected Stripe yet.</span>
                </div>
              )}
              <p className="text-xs text-[#6B6A68] mt-2 max-w-xl">
                Application fee: <strong className="text-[#1C1B1A]">{fmtBps(cfg.your_fan_cut_bps)}</strong> of every fan charge.
                {cfg.has_fan_cut_override && " (Custom rate set by platform admin.)"}
              </p>
            </div>
            <div className="flex gap-2">
              {connectStatus === "ready" ? (
                expressDashUrl ? (
                  <a href={expressDashUrl} target="_blank" rel="noopener noreferrer" data-testid="open-express-dashboard"
                    className="text-sm px-4 py-2 rounded-md border hairline bg-white text-[#1C1B1A] hover:border-[#4A5D4E] hover:text-[#4A5D4E] inline-flex items-center gap-1.5">
                    <ArrowSquareOut size={14} /> Express dashboard
                  </a>
                ) : (
                  <button type="button" disabled data-testid="open-express-dashboard-loading"
                    className="text-sm px-4 py-2 rounded-md border hairline bg-white text-[#6B6A68] inline-flex items-center gap-1.5 opacity-60 cursor-not-allowed">
                    <ArrowSquareOut size={14} /> Preparing…
                  </button>
                )
              ) : (
                <button onClick={startConnectOnboarding} disabled={busy} data-testid="start-connect-onboarding"
                  className="text-sm px-5 py-2.5 rounded-md bg-[#4A5D4E] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1.5 disabled:opacity-60 shadow-sm">
                  <Storefront size={14} weight="duotone" /> {connectStatus === "pending" ? "Continue onboarding" : "Connect Stripe"}
                </button>
              )}
            </div>
          </div>
        </Card>

        {/* Tax Info · W-9 & 1099-NEC — Feb 28 2026 v6 (Randy Compliance Pass 3).
            Placed right after Stripe Connect since it's the natural next step
            for any creator earning cash payouts. Idle state is unobtrusive; the
            panel escalates its own presentation as the user nears the $600 bar. */}
        <TaxInfoPanel testid="account-settings-tax-info" />

        {/* BYOK / Vouchers / Trackers — Jun 2026 (Randy): the single "Bring
            your own LLM keys" card is now a 3-tab advanced area (Key Setup ·
            Vouchers · Key Trackers) mirroring the dashboard Operations
            trackers. `?focus=byok` deep-links + scrolls here. */}
        <div data-testid="account-settings-byok-section">
          <KeySetupTabs user={user} initialTab={focusParam === "vouchers" ? "vouchers" : undefined} />
        </div>

        {/* YouTube API key (May 12, 2026) — same synced-replica pattern as
            BYOK. Lets creators set up YouTube ingestion from billing
            without context-switching to Silo Operations. Writes to the
            same `users.youtube_api_key` field. */}
        <div data-testid="account-settings-youtube-section">
          <YouTubeKeyCard user={user} onUpdated={(u) => setUser(u)} />
        </div>

        {/* Per-silo Fan Pricing moved to Silo Settings (Feb 18, 2026). The
            canonical home for per-silo monetization is the silo's own
            Settings page — having it in two places caused stale state
            between the two surfaces. */}

        {/* Site name + Public profile moved to the Dashboard "Profile" tab
            (Feb 18, 2026). They're identity-level (not billing-level), so
            they live with the other Profile controls now. */}

        {/* iter-40d — Beneficiary card. Affiliate only (Feb 18, 2026) —
            non-affiliates have no recurring residuals to inherit, so the
            card was clutter for them. Auto-shows once they pay the gate. */}
        <RoleGate role="affiliate">
          <BeneficiaryCard />
        </RoleGate>

        {/* Danger Zone — iter-35a */}
        {user?.role !== "super_admin" && <DangerZoneCard nav={nav} />}

        {user?.role === "super_admin" && (
          <div className="text-right">
            <Link to="/admin" data-testid="back-to-admin" className="text-sm text-[#6B6A68] hover:text-[#4A5D4E] inline-flex items-center gap-1.5">
              <ShieldCheck size={14} weight="duotone" /> Back to Super Admin
            </Link>
          </div>
        )}
        <ContactAdminFooter />

        {/* Continue → Dashboard (May 11, 2026) — funnel exit point for
            newly-paid creators landing on Account & Billing after Stripe
            checkout. Always visible (any role can use it).

            Iter160o (Randy pt-siloart-loosetext-sweep-2, Feb 2026) —
            swapped the outer `border-t hairline` for `border-t
            border-[#E5E5E0]` so the "All set? Head to your dashboard…"
            help sentence below isn't excluded from the whitetext rule
            via the `.hairline` marker (same trap as the YTRE + Earnings
            headers). The help div itself carries `.silo-loose`. */}
        <div className="mt-8 pt-6 border-t border-[#E5E5E0] flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs text-[#6B6A68] max-w-md silo-loose">
            All set? Head to your dashboard to build your first silo.
          </div>
          <Link
            to="/dashboard"
            data-testid="account-settings-continue"
            className="text-sm px-6 py-3 rounded-md bg-[#C16E5A] text-white hover:bg-[#A85E4A] flex items-center gap-2 shadow-sm transition"
          >
            Go to your dashboard <ArrowSquareOut size={14} weight="duotone" />
          </Link>
        </div>
      </main>
    </div>
  );
}

function SiloFanPriceRow({ silo, saving, connectReady, onSave, onConnectCTA }) {
  const [priceDollars, setPriceDollars] = useState(Math.round((silo.subscriber_price_cents || 0) / 100));
  const [tier, setTier] = useState(silo.access_tier || "public");
  const dirty = Math.round((silo.subscriber_price_cents || 0) / 100) !== priceDollars || (silo.access_tier || "public") !== tier;

  const wantsPaid = (priceDollars > 0) || tier !== "public";
  const needsConnect = wantsPaid && !connectReady;

  return (
    <div className="py-4 flex items-center gap-4 flex-wrap" data-testid={`fan-price-row-${silo.slug}`}>
      <div className="flex-1 min-w-[200px]">
        <div className="text-sm font-medium text-[#1C1B1A]">{silo.name}</div>
        <div className="text-xs text-[#6B6A68] font-mono">/s/{silo.slug}</div>
      </div>
      <div className="flex items-center gap-2">
        <select value={tier} onChange={(e) => setTier(e.target.value)} data-testid={`fan-tier-${silo.slug}`}
          className="bg-white border hairline rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]">
          <option value="public">Public</option>
          <option value="subscriber">Subscriber</option>
          <option value="premium">Premium</option>
        </select>
        <div className="flex items-center gap-1 border hairline rounded-md bg-white px-2 py-1.5">
          <span className="text-[#6B6A68] text-sm">$</span>
          <FreeNumberInput min={0} max={1000} step={1} value={priceDollars}
            onChange={(v) => setPriceDollars(v)}
            data-testid={`fan-price-${silo.slug}`}
            className="w-16 bg-transparent text-sm focus:outline-none tabular-nums" />
          <span className="text-xs text-[#6B6A68]">/mo</span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {needsConnect ? (
          <button onClick={onConnectCTA} data-testid={`fan-connect-cta-${silo.slug}`}
            className="text-xs px-3 py-1.5 rounded-md bg-[#4A5D4E] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1">
            <Storefront size={12} /> Connect Stripe first
          </button>
        ) : (
          <button onClick={() => onSave(silo, priceDollars * 100, tier)}
            disabled={saving || !dirty} data-testid={`fan-save-${silo.slug}`}
            className="text-xs px-3 py-1.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-40 disabled:cursor-not-allowed">
            {saving ? "Saving…" : "Save"}
          </button>
        )}
      </div>
    </div>
  );
}

// iter-35a: Danger Zone — soft-delete with 30-day grace.
function DangerZoneCard({ nav }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const me = getUser();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/me/account/status");
      setStatus(r.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const initiate = async () => {
    if (busy) return;
    if ((confirmEmail || "").toLowerCase().trim() !== (me?.email || "").toLowerCase()) {
      toast.error("Email confirmation does not match.");
      return;
    }
    if (!window.confirm("Final confirmation — schedule account deletion in 30 days?")) return;
    setBusy(true);
    try {
      await api.post("/me/account/delete", { confirm_email: confirmEmail, reason });
      toast.success("Account scheduled for deletion in 30 days.");
      setConfirming(false);
      setConfirmEmail("");
      setReason("");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not schedule deletion.");
    } finally { setBusy(false); }
  };

  const reactivate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/me/account/reactivate");
      toast.success("Account reactivated. Welcome back.");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reactivate.");
    } finally { setBusy(false); }
  };

  if (loading) return null;

  const pending = status?.status === "pending_deletion";

  return (
    <div className="bg-white border-2 border-[#C16E5A]/30 rounded-xl p-6 shadow-sm" data-testid="danger-zone-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-md bg-[#FDF2EE] text-[#C16E5A] flex items-center justify-center shrink-0">
          <Warning size={20} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-serif-display text-xl text-[#C16E5A]">Danger Zone</h3>
          <p className="text-sm text-[#6B6A68] mt-0.5">
            Account deletion has a <strong>30-day grace window</strong> — you can reactivate any time before then.
          </p>
        </div>
      </div>

      {pending ? (
        <div className="bg-[#FDF2EE] border border-[#C16E5A]/40 rounded-md p-4" data-testid="pending-deletion-banner">
          <div className="flex items-center gap-2 text-[#C16E5A] mb-2">
            <Clock size={16} weight="duotone" />
            <span className="font-medium text-sm">Account scheduled for deletion in <span className="tabular-nums" data-testid="days-remaining">{status?.days_remaining}</span> day{status?.days_remaining === 1 ? "" : "s"}.</span>
          </div>
          <p className="text-xs text-[#6B6A68] mb-3 leading-relaxed">
            Your silos are private and platform billing is paused. Reactivate now to undo this and restore full access.
          </p>
          <button onClick={reactivate} disabled={busy} data-testid="reactivate-btn"
            className="text-sm px-4 py-2 rounded-md bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] disabled:opacity-60 flex items-center gap-1.5">
            <ArrowCounterClockwise size={14} /> Reactivate my account
          </button>
        </div>
      ) : confirming ? (
        <div className="bg-[#FDF2EE] border border-[#C16E5A]/30 rounded-md p-4 space-y-3" data-testid="delete-confirm-form">
          <p className="text-sm text-[#1C1B1A]">
            Type <code className="text-[#C16E5A] bg-white px-1.5 py-0.5 rounded">{me?.email}</code> below to confirm.
          </p>
          <input
            type="email" value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)}
            placeholder="Confirm your email"
            data-testid="delete-confirm-email"
            className="w-full text-sm px-3 py-2 border hairline rounded-md bg-white"
          />
          <textarea
            value={reason} onChange={(e) => setReason(e.target.value.slice(0, 500))}
            placeholder="Optional — why are you leaving? (max 500 chars)"
            data-testid="delete-reason"
            className="w-full text-sm px-3 py-2 border hairline rounded-md bg-white min-h-[80px]"
          />
          <div className="flex gap-2">
            <button onClick={initiate} disabled={busy} data-testid="delete-confirm-btn"
              className="text-sm px-4 py-2 rounded-md bg-[#C16E5A] text-white hover:bg-[#A85E4A] disabled:opacity-60 flex items-center gap-1.5">
              <Trash size={14} /> Schedule deletion (30-day grace)
            </button>
            <button onClick={() => { setConfirming(false); setConfirmEmail(""); setReason(""); }}
              data-testid="delete-cancel-btn"
              className="text-sm px-4 py-2 rounded-md border hairline bg-white text-[#6B6A68] hover:text-[#1C1B1A]">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-[#6B6A68]">
            Read the full <Link to="/policy/account-termination" className="text-[#4A5D4E] underline" data-testid="policy-link">Account Termination Policy</Link> before deleting.
          </div>
          <button
            onClick={() => setConfirming(true)}
            data-testid="delete-account-btn"
            className="text-sm px-4 py-2 rounded-md border-2 border-[#C16E5A] text-[#C16E5A] bg-white hover:bg-[#C16E5A] hover:text-white transition-colors flex items-center gap-1.5"
          >
            <Trash size={14} /> Delete my account
          </button>
        </div>
      )}
    </div>
  );
}

// "Contact admin" footer card. Same component as the silo-report dialog,
// but the form's intro and email subject change based on context. Visible
// to all logged-in users.
export function ContactAdminFooter() {
  const [open, setOpen] = useState(false);
  return (
    <>
      {/* Iter160o (Randy pt-siloart-loosetext-sweep-2) — "Contact admin"
          eyebrow button is visually a link on the canvas (naked, no
          card). `.silo-loose` opts the button into the whitetext rule
          override so it doesn't stay dark against dark art. */}
      <div className="text-center mt-8 mb-4 text-[10px] uppercase tracking-[0.2em]">
        <button onClick={() => setOpen(true)} data-testid="contact-admin-btn"
          className="text-[#6B6A68] hover:text-[#4A5D4E] silo-loose">
          Contact admin
        </button>
      </div>
      <ReportContactModal open={open} onClose={() => setOpen(false)} context="general" />
    </>
  );
}


// iter-40d — Beneficiary email card. Wires up the standing /api/me/beneficiary
// endpoint that the affiliate exit policy module reads when an affiliate goes
// MIA / passes away / has their account closed. Without this UI the field has
// existed in the DB since iter-39 but no creator could populate it.
function BeneficiaryCard() {
  const [email, setEmail] = useState("");
  const [snapshot, setSnapshot] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get("/me/beneficiary");
        if (!cancel) {
          const v = r.data?.beneficiary_email || "";
          setEmail(v);
          setSnapshot(v);
        }
      } catch { /* silently — not all users have this set yet */ }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, []);

  const dirty = email.trim().toLowerCase() !== (snapshot || "").toLowerCase();

  const save = async () => {
    setSaving(true);
    try {
      const trimmed = email.trim();
      if (trimmed && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
        toast.error("That doesn't look like a valid email address.");
        setSaving(false);
        return;
      }
      const r = await api.patch("/me/beneficiary", { beneficiary_email: trimmed || null });
      const v = r.data?.beneficiary_email || "";
      setEmail(v);
      setSnapshot(v);
      toast.success(trimmed ? "Beneficiary email saved." : "Beneficiary email cleared.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save beneficiary email.");
    } finally { setSaving(false); }
  };

  return (
    <Card
      title="Beneficiary email"
      subtitle="If you ever go missing, pass away, or have your account closed, this email receives any accrued Partner Program payouts + remaining unpaid payouts. Optional — leave blank to default to your own email."
      icon={HeartStraight}
      testid="beneficiary-card"
    >
      <div className="flex items-end gap-3 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <label className="block text-sm text-[#6B6A68] mb-1.5">Beneficiary email</label>
          <input
            type="email"
            data-testid="beneficiary-email-input"
            placeholder={loading ? "Loading…" : "name@example.com  (or leave blank)"}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading || saving}
            className="w-full bg-white border hairline rounded-md px-4 py-3 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] text-sm"
          />
        </div>
        <button
          type="button"
          onClick={save}
          disabled={!dirty || saving || loading}
          data-testid="beneficiary-save-btn"
          className="px-4 py-3 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] text-sm flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FloppyDisk size={14} weight="duotone" /> {saving ? "Saving…" : "Save"}
        </button>
      </div>
      {snapshot && !dirty && (
        <p className="text-[11px] text-[#4A5D4E] mt-2 flex items-center gap-1">
          <CheckCircle size={11} weight="fill" /> On file: <span className="font-mono">{snapshot}</span>
        </p>
      )}
      <p className="text-[11px] text-[#6B6A68] mt-2 italic">
        We never contact this address proactively — it's only used to route post-account-close payouts.
      </p>
    </Card>
  );
}

// Phase A+ — "Site name" / operator handle card. Lightweight: just a string
// on the user record exposed via PATCH /api/me/site-name. Drives the
// "Invited by <Site name>" pill on public Q&A pages. Empty falls back to
// user.name on the backend.
export function SiteNameCard() {
  const [siteName, setSiteName] = useState("");
  const [snapshot, setSnapshot] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const fallback = (getUser()?.name || "").trim();

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get("/me/site-name");
        if (!cancel) {
          const v = r.data?.site_name || "";
          setSiteName(v);
          setSnapshot(v);
        }
      } catch { /* not fatal */ }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, []);

  const trimmed = siteName.trim();
  const dirty = trimmed !== (snapshot || "").trim();

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/me/site-name", { site_name: trimmed || null });
      const v = r.data?.site_name || "";
      setSiteName(v);
      setSnapshot(v);
      toast.success(trimmed ? "Site name saved." : "Site name cleared.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save site name.");
    } finally { setSaving(false); }
  };

  const previewLabel = trimmed || fallback || "Your name";

  return (
    <Card
      title="Site name"
      subtitle="The handle fans see when you invite them across My Silo. Shows up as 'Invited by <site name>' on the public Q&A header. Defaults to your real name when empty."
      icon={Storefront}
      testid="site-name-card"
    >
      <div className="flex items-end gap-3 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <label className="block text-sm text-[#6B6A68] mb-1.5">Site name</label>
          <input
            type="text"
            data-testid="site-name-input"
            placeholder={loading ? "Loading…" : "e.g. your brand or studio name"}
            value={siteName}
            onChange={(e) => setSiteName(e.target.value.slice(0, 40))}
            disabled={loading || saving}
            maxLength={40}
            className="w-full bg-white border hairline rounded-md px-4 py-3 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] text-sm"
          />
        </div>
        <button
          type="button"
          onClick={save}
          disabled={!dirty || saving || loading}
          data-testid="site-name-save-btn"
          className="px-4 py-3 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] text-sm flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FloppyDisk size={14} weight="duotone" /> {saving ? "Saving…" : "Save"}
        </button>
      </div>
      <p className="text-[11px] text-[#6B6A68] mt-3" data-testid="site-name-preview">
        Preview: <span className="text-[#1C1B1A]">Invited by <strong className="uppercase tracking-[0.15em] font-medium">{previewLabel}</strong></span>
      </p>
      <p className="text-[10px] text-[#6B6A68] mt-1 italic">
        Up to 40 characters. Use whatever you want fans to associate with your invitation links.
      </p>
    </Card>
  );
}

// Profile card (Feb 13, 2026) — bio + avatar URL for public /u/<code> page.
// Both fields optional; saving an empty bio clears it. We don't host avatar
// uploads — pasting a URL keeps this lightweight and works with any image
// service the creator already uses.
export function ProfileCard() {
  const [bio, setBio] = useState("");
  const [avatar, setAvatar] = useState("");
  // Feb 14, 2026 PM session #5 — Silo Wars privacy toggle.
  const [battleHistoryPublic, setBattleHistoryPublic] = useState(false);
  // Feb 18, 2026 — searchable identity fields (Phase 1 of unified search).
  // Each handle has its own visibility toggle; defaults OFF.
  const [displayName, setDisplayName] = useState("");
  const [website, setWebsite] = useState("");
  const [handles, setHandles] = useState({ youtube: "", twitter: "", instagram: "", tiktok: "", twitch: "" });
  const [visibility, setVisibility] = useState({ youtube: false, twitter: false, instagram: false, tiktok: false, twitch: false, website: false });
  const [refCode, setRefCode] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        // /auth/me has the bio + avatar + battle-history privacy toggle.
        const [meR, refR] = await Promise.all([
          api.get("/auth/me"),
          api.get("/referrals/me").catch(() => ({ data: {} })),
        ]);
        if (cancel) return;
        const m = meR.data || {};
        const b = m.profile_bio || "";
        const a = m.profile_avatar_url || "";
        const pub = !!m.show_battle_history_publicly;
        const dn = m.profile_display_name || "";
        const ws = m.profile_website || "";
        const h = {
          youtube: m.profile_youtube_handle || "",
          twitter: m.profile_twitter_handle || "",
          instagram: m.profile_instagram_handle || "",
          tiktok: m.profile_tiktok_handle || "",
          twitch: m.profile_twitch_handle || "",
        };
        const v = {
          youtube: false, twitter: false, instagram: false, tiktok: false, twitch: false, website: false,
          ...(m.profile_handles_visibility || {}),
        };
        setBio(b); setAvatar(a); setBattleHistoryPublic(pub);
        setDisplayName(dn); setWebsite(ws); setHandles(h); setVisibility(v);
        setSnapshot({ bio: b, avatar: a, battleHistoryPublic: pub, displayName: dn, website: ws, handles: h, visibility: v });
        setRefCode(refR.data?.referral_code || null);
      } catch { /* not fatal */ }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, []);

  const dirty = !!snapshot && (
    bio.trim() !== (snapshot.bio || "").trim()
    || avatar.trim() !== (snapshot.avatar || "").trim()
    || battleHistoryPublic !== !!snapshot.battleHistoryPublic
    || displayName.trim() !== (snapshot.displayName || "").trim()
    || website.trim() !== (snapshot.website || "").trim()
    || JSON.stringify(handles) !== JSON.stringify(snapshot.handles)
    || JSON.stringify(visibility) !== JSON.stringify(snapshot.visibility)
  );

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        profile_bio: bio.trim(),
        profile_avatar_url: avatar.trim(),
        show_battle_history_publicly: battleHistoryPublic,
        profile_display_name: displayName.trim(),
        profile_website: website.trim(),
        profile_youtube_handle: handles.youtube.trim(),
        profile_twitter_handle: handles.twitter.trim(),
        profile_instagram_handle: handles.instagram.trim(),
        profile_tiktok_handle: handles.tiktok.trim(),
        profile_twitch_handle: handles.twitch.trim(),
        profile_handles_visibility: visibility,
      };
      await api.patch("/me/profile", payload);
      setSnapshot({
        bio: payload.profile_bio,
        avatar: payload.profile_avatar_url,
        battleHistoryPublic,
        displayName: payload.profile_display_name,
        website: payload.profile_website,
        handles: { ...handles },
        visibility: { ...visibility },
      });
      toast.success("Profile saved.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save profile.");
    } finally { setSaving(false); }
  };

  const profileUrl = refCode ? `/u/${refCode}` : null;

  const visibleCount = Object.values(visibility).filter(Boolean).length;
  const handleRow = (key, label, placeholder) => (
    <div className="flex items-center gap-3" key={key}>
      <div className="flex-1 min-w-0">
        <label className="block text-[11px] uppercase tracking-[0.22em] text-[#6B6A68] mb-1">{label}</label>
        <input
          type="text"
          data-testid={`profile-handle-${key}`}
          placeholder={placeholder}
          value={handles[key]}
          onChange={(e) => setHandles({ ...handles, [key]: e.target.value.slice(0, 240) })}
          disabled={loading || saving}
          className="w-full bg-white border hairline rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] text-sm"
        />
      </div>
      <label className="flex items-center gap-1.5 text-[11px] text-[#6B6A68] cursor-pointer shrink-0 pt-5">
        <input
          type="checkbox"
          checked={!!visibility[key]}
          onChange={(e) => setVisibility({ ...visibility, [key]: e.target.checked })}
          disabled={loading || saving || !handles[key].trim()}
          data-testid={`profile-handle-${key}-visible`}
          className="w-3.5 h-3.5 accent-[#4A5D4E]"
        />
        Searchable
      </label>
    </div>
  );

  return (
    <Card
      title="Public profile"
      subtitle="Bio, avatar, and the handles people might know you by. Each handle has its own searchable toggle — flip ON only the ones you want people to find you with."
      icon={UserIcon}
      testid="profile-card"
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm text-[#6B6A68] mb-1.5">Display name <span className="text-[#A89360]">(how your name shows in search)</span></label>
          <input
            type="text"
            data-testid="profile-display-name"
            placeholder={loading ? "Loading…" : "e.g. your name or brand"}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value.slice(0, 80))}
            disabled={loading || saving}
            className="w-full bg-white border hairline rounded-md px-4 py-3 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] text-sm"
          />
        </div>
        <div>
          <label className="block text-sm text-[#6B6A68] mb-1.5">Bio</label>
          <textarea
            data-testid="profile-bio-input"
            placeholder={loading ? "Loading…" : "A short line or two about what you build, write, or curate."}
            value={bio}
            onChange={(e) => setBio(e.target.value.slice(0, 500))}
            disabled={loading || saving}
            rows={3}
            maxLength={500}
            className="w-full bg-white border hairline rounded-md px-4 py-3 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] text-sm resize-y"
          />
          <div className="text-[10px] text-[#6B6A68] mt-1">{bio.length} / 500</div>
        </div>
        <div>
          <label className="block text-sm text-[#6B6A68] mb-1.5">Avatar URL <span className="text-[#A89360]">(optional)</span></label>
          <input
            type="url"
            data-testid="profile-avatar-input"
            placeholder="https://example.com/me.jpg"
            value={avatar}
            onChange={(e) => setAvatar(e.target.value.slice(0, 500))}
            disabled={loading || saving}
            className="w-full bg-white border hairline rounded-md px-4 py-3 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] text-sm"
          />
        </div>

        {/* Feb 18, 2026 — searchable handles + per-handle visibility. */}
        <div className="border-t border-[#E8E5DF] pt-4">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div>
              <div className="text-sm font-medium text-[#1C1B1A]">Where else can people find you?</div>
              <p className="text-[11px] text-[#6B6A68] mt-0.5 max-w-md leading-relaxed">
                Add the handles you're known by elsewhere. <strong>Each one is private until you flip "Searchable" on</strong> — that way nothing gets exposed by accident. <em className="text-[#4A5D4E]">Have more than one? Separate with commas — all still searchable.</em>
              </p>
            </div>
            <span className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68] shrink-0" data-testid="profile-handles-visible-count">{visibleCount} searchable</span>
          </div>
          <div className="space-y-3">
            {handleRow("youtube", "YouTube", "channel handle (no @)")}
            {handleRow("twitter", "X / Twitter", "handle (no @)")}
            {handleRow("instagram", "Instagram", "handle (no @)")}
            {handleRow("tiktok", "TikTok", "handle (no @)")}
            {handleRow("twitch", "Twitch", "handle (no @)")}
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <label className="block text-[11px] uppercase tracking-[0.22em] text-[#6B6A68] mb-1">Website</label>
                <input
                  type="url"
                  data-testid="profile-website"
                  placeholder="https://your-site.com"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value.slice(0, 240))}
                  disabled={loading || saving}
                  className="w-full bg-white border hairline rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] text-sm"
                />
              </div>
              <label className="flex items-center gap-1.5 text-[11px] text-[#6B6A68] cursor-pointer shrink-0 pt-5">
                <input
                  type="checkbox"
                  checked={!!visibility.website}
                  onChange={(e) => setVisibility({ ...visibility, website: e.target.checked })}
                  disabled={loading || saving || !website.trim()}
                  data-testid="profile-website-visible"
                  className="w-3.5 h-3.5 accent-[#4A5D4E]"
                />
                Show on profile
              </label>
            </div>
          </div>
        </div>

        {/* Feb 14, 2026 PM session #5 — Silo Wars privacy toggle. Default
            off — only YOU see your past battles. Flip on to let other
            logged-in users browse your saved archive after they find you
            via category search. */}
        <div className="border-t border-[#E8E5DF] pt-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={battleHistoryPublic}
              onChange={(e) => setBattleHistoryPublic(e.target.checked)}
              disabled={loading || saving}
              data-testid="profile-battle-history-toggle"
              className="mt-1 w-4 h-4 accent-[#4A5D4E]"
            />
            <div className="flex-1">
              <div className="text-sm font-medium text-[#1C1B1A]">
                Show my Silo Wars history publicly
              </div>
              <p className="text-xs text-[#6B6A68] mt-0.5 max-w-md leading-relaxed">
                When ON, other logged-in users can find you via category search and watch your saved past battles. When OFF (default), your archive stays private. You can still battle in public either way — this only controls who sees your past history.
              </p>
            </div>
          </label>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={save}
            disabled={!dirty || saving || loading}
            data-testid="profile-save-btn"
            className="px-4 py-2.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] text-sm flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FloppyDisk size={14} weight="duotone" /> {saving ? "Saving…" : "Save profile"}
          </button>
          {profileUrl && (
            <a href={profileUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-[#C16E5A] hover:underline flex items-center gap-1" data-testid="profile-public-link">
              View public profile <ArrowSquareOut size={12} weight="duotone" />
            </a>
          )}
        </div>
      </div>
    </Card>
  );
}
